<script lang="ts" setup>
import { Image, showFailToast, showLoadingToast, showSuccessToast } from "vant";
import PlusIcon from "@/components/icon/PlusIcon.vue";
import { ref } from "vue";
import { MediaUploadFileItem } from "@/types/component";
import MediaSelectPopup from "@/components/mobile/media/MediaSelectPopup.vue";
import { useStory } from "@/stores/story";
import { Media, Story } from "@/types/entity";
import FontPlusIcon from "@/components/icon/FontPlusIcon.vue";
import { useRouter } from "vue-router";
import i18n from "@/locales";
import { useMate } from "@/stores/mate";

const props = withDefaults(
  defineProps<{
    storyBookId?: number; // 创建story时必传
    story?: Story;
    storyId?: number;
    title?: string;
    description?: string;
    media?: Media;
    videoMedia?: Media;
    enableEditVideo?: boolean;
  }>(),
  {
    title: "",
    description: "",
    enableEditVideo: false,
  }
);
const emit = defineEmits<{
  (e: "onSuccess", story: Story): void;
  (e: "onCancel"): void;
}>();

// State
const storyStore = useStory();
const router = useRouter();
const title = ref(props.title);
const description = ref(props.description);
const mediaList = ref<MediaUploadFileItem[]>(
  props.media
    ? [
        {
          mediaId: props.media!.id,
          url: props.media!.url,
          status: "done",
        } as MediaUploadFileItem,
      ]
    : []
);

const videoMediaList = ref<MediaUploadFileItem[]>(
  props.videoMedia
    ? [
        {
          mediaId: props.videoMedia!.id,
          url: props.videoMedia!.url,
          videoCoverUrl: props.videoMedia!.videoCoverUrl,
          status: "done",
        } as MediaUploadFileItem,
      ]
    : []
);
const showMediaSelection = ref(false);
const showVideoMediaSelection = ref(false);
// EventHandler
const onConfirmStoryCreateInfo = async () => {
  // 1. 校验
  if (!title.value) {
    showFailToast({ message: i18n.global.t("Message.enterChapterName") });
    return;
  }
  if (!description.value) {
    showFailToast({
      message: i18n.global.t("Message.enterChapterDescription"),
    });
    return;
  }
  // 2. 提交
  const loadingToast = showLoadingToast({
    message: props.storyId
      ? i18n.global.t("Message.savingChapter")
      : i18n.global.t("Message.creatingChapter"),
    duration: 0,
  });
  const params = {
    title: title.value,
    description: description.value,
    mediaId: mediaList.value[0]?.mediaId,
    videoMediaId: videoMediaList.value[0]?.mediaId,
    visibility: props.story?.visibility || 2,
    mateId: useMate().userMate.id,
  };
  try {
    if (props.storyId) {
      const story = await storyStore.updateStory(props.storyId, params);
      showSuccessToast(i18n.global.t("Message.chapterModifiedSuccessfully"));
      emit("onSuccess", story);
    } else {
      const story = await storyStore.createStory({
        ...params,
        storyBookId: props.storyBookId!,
      });
      router.push({
        name: "storyEditor",
        params: { uuid: story.uuid },
      });
      showSuccessToast(i18n.global.t("Message.chapterCreatedSuccessfully"));
      emit("onSuccess", story);
    }
  } finally {
    loadingToast.close();
  }
};
const reset = () => {
  title.value = props.title;
  description.value = props.description;
  mediaList.value = props.media
    ? [
        {
          mediaId: props.media!.id,
          url: props.media!.url,
          status: "done",
        } as MediaUploadFileItem,
      ]
    : [];
};

export interface StoryInfoEditExpose {
  reset: () => void;
}

defineExpose<StoryInfoEditExpose>({ reset });
</script>
<template>
  <div
    class="flex flex-col items-center justify-center w-full h-full gap-5 px-10"
  >
    <div class="flex flex-col items-center justify-center gap-2 w-full">
      <font-plus-icon class="w-[20%] text-base-content/50" />
      <div class="text-lg font-bold text-base-content/50">
        {{ storyId ? $t("story.chapterEditing") : $t("story.newChapter") }}
      </div>
    </div>
    <div class="flex flex-col gap-2 w-full">
      <div class="flex flex-col gap-2">
        <span class="font-bold">{{ $t("story.chapterTitle") }}</span>
        <input v-model="title" class="input input-bordered" />
      </div>
      <div class="flex flex-col gap-2">
        <span class="font-bold">{{ $t("story.chapterDescription") }}</span>
        <textarea v-model="description" class="textarea textarea-bordered" />
      </div>
      <div class="flex flex-col gap-2">
        <span class="font-bold">{{ $t("story.chapterCover") }}</span>
        <div class="w-full flex justify-center">
          <div
            class="relative w-28 aspect-[1/1]"
            @click="showMediaSelection = true"
          >
            <Image
              v-if="mediaList.length > 0"
              :src="mediaList[0].url!"
              class="w-full aspect-[1/1]"
              fit="cover"
              crossorigin="anonymous"
            />
            <div
              v-else
              class="absolute top-0 left-0 flex items-center justify-center btn"
            >
              <plus-icon class="icon-xl text-base" />
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2" v-if="story">
        <span class="font-bold">{{ $t("story.visibility") }}</span>
        <visibility-select v-model:visibility="story!.visibility" />
      </div>
      <!-- <div v-if="enableEditVideo" class="flex flex-col gap-2">
        <span class="font-bold">{{ $t("story.chapterCoverVideo") }}</span>
        <div class="w-full flex justify-center gap-2">
          <div class="relative w-40 aspect-[1/1]">
            <video
              v-if="videoMediaList.length > 0"
              :poster="videoMediaList[0].videoCoverUrl"
              :src="videoMediaList[0].url"
              class="w-full aspect-[1/1]"
              controls
              crossorigin="anonymous"
            />
            <div
              v-else
              class="absolute top-0 left-0 flex items-center justify-center btn"
              @click="showVideoMediaSelection = true"
            >
              <plus-icon class="icon-xl text-base" />
            </div>
          </div>
          <div class="flex items-end" @click="showVideoMediaSelection = true">
            <button v-if="videoMediaList.length > 0" class="btn btn-xs">
              {{ $t("Buttons.replace") }}
            </button>
          </div>
        </div>
      </div> -->
    </div>
    <div class="flex flex-col gap-2 justify-between items-center w-full">
      <button
        class="btn rounded btn-primary btn-sm w-full"
        @click="onConfirmStoryCreateInfo"
      >
        {{ $t("Buttons.save") }}
      </button>
      <button class="btn rounded btn-sm w-full" @click="emit('onCancel')">
        {{ $t("Buttons.cancel") }}
      </button>
    </div>
  </div>
  <media-select-popup
    v-model:media-list="mediaList"
    v-model:show="showMediaSelection"
    :cropAspectRatio="1"
    :enable-crop="true"
    :max-count="1"
    :multiple="false"
    :upload-type="8"
    media-type="img"
    replace
  />
  <media-select-popup
    v-if="enableEditVideo"
    v-model:media-list="videoMediaList"
    v-model:show="showVideoMediaSelection"
    :enable-go-image-gen="false"
    :max-count="1"
    :multiple="false"
    :upload-type="8"
    media-type="video"
    replace
  />
</template>
